import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Tabs, Tab, Typography, useMediaQuery, useTheme, Skeleton, Stack } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ApplicantTabs from "../common-features/ApplicantTabs";
import ConsentDetails from "./ConsentDetails";
import type { RootState } from "../../services/redux/store";
import { setAdditionalSummaryActiveTab, setAdditionalSummaryConsentComplete, setAdditionalSummaryDocumentsComplete } from "../../services/redux/slices/tabsSlice";
import { setFetchUserApplicationData } from "../../services/redux/slices/stepperDynamicFormSlice";
import { setCoApplicantDetails as setCoApplicantDetailsRedux } from "../../services/redux/slices/applicationLoanDetailsSlice";
import { fetchUserApplicationsFormData } from "../../services/api/service/userApplicationService";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import {getCoApplicantList } from "../../utils/helpers";
interface AdditionalSummaryDetailProps {
  tabsList?: any;
  applicationDetail?: any;
  mainApplicantDetails?: any;
  coApplicantDetails?: any;
  handleFetchApplicationDetail?: () => void;
  partnerStartedJourney?: boolean | null;
  dropdownData?: any;
  applicationStatus?: string;
  coApplicantEmploymentType?: string;
  isPayPartyPresent?: boolean;
  isPayPartyValue?: string;
  activeHorizontalIndex?:any;
  activeVerticalIndex?:any;
}

// Groups co-applicant fields by applicant number, mirroring Summary.tsx's getCoApplicationDetail
// so the two screens derive identical coApplicantDetails from the same API response.
const getCoApplicationDetail = (fieldsList: any) => {
  const grouped: Record<string, any> = {};
  fieldsList?.forEach((item: any) => {
    const [key, value] = Object.entries(item || {})[0] || [];
    if (!key) return;
    const match = key.match(/basicCoApplicant(\d+)(.+)/);
    if (match) {
      const [, groupNumber, field] = match;
      const coApplicantKey = `coapplicant${groupNumber}`;
      if (!grouped[coApplicantKey]) grouped[coApplicantKey] = {};
      grouped[coApplicantKey][field] = value;
    }
  });
  return grouped;
};

// Shown full-screen in place of the Documents tab while ApplicantTabs runs its initial
// mandatory-documents check across every applicant, so the user never sees a tab/document
// list flash in before we actually know whether everything required is already uploaded.
const MandatoryDocumentsCheckSkeleton: React.FC<{ isXsOrSm: boolean }> = ({ isXsOrSm }) => (
  <Box sx={{ width: "100%", minHeight: "70vh" }}>
    <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
      {[0, 1, 2].map((idx) => (
        <Skeleton
          key={idx}
          variant="rounded"
          width={isXsOrSm ? 110 : 140}
          height={34}
          sx={{ borderRadius: "20px" }}
        />
      ))}
    </Stack>
    <Stack spacing={2}>
      {[0, 1, 2, 3].map((idx) => (
        <Box key={idx}>
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={72} sx={{ borderRadius: "12px" }} />
        </Box>
      ))}
    </Stack>
  </Box>
);

const AdditionalSummaryDetail: React.FC<AdditionalSummaryDetailProps> = ({
  tabsList,
  applicationDetail,
  mainApplicantDetails,
  coApplicantDetails,
  handleFetchApplicationDetail,
  partnerStartedJourney,
  dropdownData,
  applicationStatus,
  coApplicantEmploymentType,
  isPayPartyPresent,
  isPayPartyValue,
  activeHorizontalIndex,
  activeVerticalIndex,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const dispatch = useDispatch();
  const activeTab = useSelector((state: RootState) => state.tabs.additionalSummaryActiveTab);
  
  const stepper = useSelector((state: RootState) => state.stepper);
  const appLoanData = useSelector((state: RootState) => state.applicationLoanDetailsData);
  const tabsState = useSelector((state: RootState) => state.tabs);
  const [fetchedApplicationData, setFetchedApplicationData] = useState<any>(null);

  // ApplicantTabs runs a mandatory-documents check across every applicant right after it
  // mounts (see its syncAdditionalSummaryDocuments effect). Until that first check fully
  // succeeds, keep the Documents tab covered with a full-screen skeleton instead of letting
  // the user see a possibly-incomplete tab/document list while the redirect-in check is
  // still in flight.
  const [isCheckingMandatoryDocuments, setIsCheckingMandatoryDocuments] = useState(true);

  // Data derived from the API response, populated the same way Summary.tsx populates
  // tabsList/mainApplicantDetails/coApplicantDetails/etc. for its own ApplicantTabs consumer.
  const [fetchedLeadId, setFetchedLeadId] = useState<string>("");
  const [fetchedTabsList, setFetchedTabsList] = useState<any>(null);
  const [fetchedMainApplicantDetails, setFetchedMainApplicantDetails] = useState<any>(null);
  const [fetchedCoApplicantDetails, setFetchedCoApplicantDetails] = useState<any>(null);
  const [fetchedPartnerStartedJourney, setFetchedPartnerStartedJourney] = useState<boolean | null>(null);
  const [fetchedCoApplicantEmploymentType, setFetchedCoApplicantEmploymentType] = useState<string>("");
  const [fetchedIsPayPartyPresent, setFetchedIsPayPartyPresent] = useState<boolean>(false);
  const [fetchedIsPayPartyValue, setFetchedIsPayPartyValue] = useState<string>("");

  const findCoApplicantNumber: number = useMemo(() => {
    return Number(String(stepper?.documentApplicantType ?? "").split(" ")?.[1]);
  }, [stepper?.documentApplicantType]);

  const fetchApplicationData = useCallback(async () => {
    try {
      const token: any = process.env.REACT_APP_FETCH_TOKEN;
      const trackingId = stepper?.trackingId || "";
      if (!trackingId) return;

      const res: any = await fetchUserApplicationsFormData(token || "", trackingId);
      if (res?.status === 200) {
        const applicationData = res?.data?.applications?.[0] || null;
        setFetchedApplicationData(applicationData);
        if (!applicationData) return;

        setFetchedLeadId(applicationData?.leadId || "");
        setFetchedPartnerStartedJourney(Boolean(applicationData?.partnerStartedJourney));

        // Keep Redux in sync so any consumer reading fetchUserApplicationData (e.g. ApplicantTabs)
        // gets valid applicantId/applicationId/partnerStartedJourney, same as Summary.tsx does.
        dispatch(setFetchUserApplicationData(applicationData));

        // Same guard Summary.tsx uses: when it's a partner-initiated journey with a leadId,
        // co-applicant/tab data comes from the partner lead-details API instead (see below).
        if (!(applicationData?.partnerStartedJourney && applicationData?.leadId)) {
          const fieldsList: any = getCoApplicantList(applicationData);
          const coApplicantNumbers = Array.from(
            new Set(
              fieldsList
                ?.map((obj: any) =>
                  Object.keys(obj || {})[0]?.match(/^basicCoApplicant(\d+)FirstName$/)?.[1]
                )
                .filter(Boolean)
                .map((num: string) => Number(num))
            )
          ).sort((a: any, b: any) => a - b);

          const sortedTabNames = coApplicantNumbers.map((number: any) => `Co-Applicant ${number}`);
          sortedTabNames?.unshift("Applicant");
          setFetchedTabsList(sortedTabNames);

          const coAppDetails = getCoApplicationDetail(fieldsList);
          setFetchedCoApplicantDetails(coAppDetails);
          dispatch(setCoApplicantDetailsRedux(coAppDetails));

          if (findCoApplicantNumber && applicationData?.horizontalSteps) {
            let employmentTypeValue = "";
            let employmentIsEmployedValue = "";
            const employmentTypeKey = `employmentCoApplicant${findCoApplicantNumber}employmentType`;
            const payPartyKey = `basicCoApplicant${findCoApplicantNumber}PayParty`;
            const employmentIsEmployedKey = `employmentCoApplicant${findCoApplicantNumber}isEmployed`;

            for (let i = 0; i < applicationData.horizontalSteps.length; i++) {
              const verticalSteps = applicationData.horizontalSteps[i]?.verticalSteps || [];
              for (let j = 0; j < verticalSteps.length; j++) {
                const fields = verticalSteps[j]?.fields || [];
                const employmentIsEmployedField = fields.find((item: any) => Object.keys(item)[0] === employmentIsEmployedKey);
                const employmentTypeField = fields.find((item: any) => Object.keys(item)[0] === employmentTypeKey);
                if (employmentTypeField) {
                  employmentTypeValue = Object.values(employmentTypeField)[0] as string;
                  employmentIsEmployedValue = Object.values(employmentIsEmployedField || {})[0] as string;

                  const payPartyField = fields.find((item: any) => Object.keys(item)[0] === payPartyKey);
                  if (payPartyField) {
                    setFetchedIsPayPartyPresent(true);
                    setFetchedIsPayPartyValue(Object.values(payPartyField)[0] as string);
                  }
                  break;
                }
              }
              if (employmentTypeValue) break;
            }
            setFetchedCoApplicantEmploymentType(employmentIsEmployedValue === "Yes" ? employmentTypeValue : "");
          }
        }
      }
    } catch (_error) {
      setFetchedApplicationData(null);
    }
  }, [dispatch, findCoApplicantNumber, stepper?.trackingId]);

  // Mirrors Summary.tsx's handleFetchPartnerLeadDetailsApi: for partner-initiated journeys,
  // co-applicant/tab/main-applicant data comes from the partner lead-details API instead of
  // the raw application form data fetched above.
  const fetchPartnerLeadDetails = useCallback(async () => {
    if (!fetchedLeadId) return;
    try {
      const payload = {
        leadId: fetchedLeadId,
        trackingId: stepper?.trackingId,
      };
      const res: any = await eligibilityCheckServices.partnerGetLeadDetailsApi(payload);
      const partnerDetails = res?.data;
      if (!partnerDetails) return;

      if (partnerDetails?.data?.applicantDetails) {
        setFetchedMainApplicantDetails(partnerDetails.data.applicantDetails);
      }

      if (partnerDetails?.data?.coApplicantDetails) {
        const partnerCoApplicantDetails = partnerDetails.data.coApplicantDetails;

        const getPartnerCoApplicationDetail = (detailsArr: any[]) => {
          const grouped: Record<string, any> = {};
          detailsArr.forEach((obj: any) => {
            const key = Object.keys(obj)[0];
            const arr = obj[key];
            grouped[key] = {};
            arr.forEach((fieldObj: any) => {
              grouped[key][fieldObj.name] = fieldObj.value;
            });
          });
          return grouped;
        };

        const partnerCoAppDetails = getPartnerCoApplicationDetail(partnerCoApplicantDetails);
        setFetchedCoApplicantDetails(partnerCoAppDetails);
        dispatch(setCoApplicantDetailsRedux(partnerCoAppDetails));

        if (findCoApplicantNumber && partnerCoApplicantDetails) {
          const coApplicantKey = `coapplicant${findCoApplicantNumber}`;
          const coApplicantData = partnerCoApplicantDetails.find(
            (item: any) => Object.keys(item)[0]?.toLowerCase() === coApplicantKey.toLowerCase()
          );
          if (coApplicantData) {
            const coApplicantFields = coApplicantData[Object.keys(coApplicantData)[0]];
            if (Array.isArray(coApplicantFields)) {
              const employmentTypeField = coApplicantFields.find(
                (field: any) => field.name === "employmentType" || field.name?.toLowerCase().includes("employmenttype")
              );
              if (employmentTypeField?.value) {
                setFetchedCoApplicantEmploymentType(employmentTypeField.value);
              }
            }
          }
        }

        const coApplicantTabNameList = partnerCoApplicantDetails.map((item: any, index: number) => {
          const key = Object.keys(item)[0];
          const numberMatch = key.match(/coapplicant(\d+)/i);
          const number = numberMatch ? parseInt(numberMatch[1], 10) : index + 1;
          return { number, name: `Co-Applicant ${number}` };
        });
        coApplicantTabNameList.sort((a: any, b: any) => a.number - b.number);
        const sortedTabNames = coApplicantTabNameList.map((item: any) => item.name);
        sortedTabNames.unshift("Applicant");
        setFetchedTabsList(sortedTabNames);
      } else {
        setFetchedTabsList(["Applicant"]);
      }
    } catch (_error) {
      // Keep whatever data we already have; a failed partner lookup shouldn't blank the tabs.
    }
  }, [dispatch, fetchedLeadId, findCoApplicantNumber, stepper?.trackingId]);

  const fetchedFormData = useMemo(() => {
    const horizontalSteps = fetchedApplicationData?.horizontalSteps || [];
    let apiFormData: Record<string, any> = {};

    for (let i = 0; i < horizontalSteps.length; i++) {
      const verticalSteps = horizontalSteps?.[i]?.verticalSteps || [];
      for (let j = 0; j < verticalSteps.length; j++) {
        const fields = verticalSteps?.[j]?.fields || [];
        for (let k = 0; k < fields.length; k++) {
          apiFormData = { ...apiFormData, ...fields[k] };
        }
      }
    }

    return apiFormData;
  }, [fetchedApplicationData]);

  const consentFormData = useMemo(
    () => ({ ...(stepper?.formData || {}), ...fetchedFormData }),
    [stepper?.formData, fetchedFormData],
  );

  useEffect(() => {
    fetchApplicationData();
  }, [fetchApplicationData]);

  useEffect(() => {
    if (fetchedPartnerStartedJourney && fetchedLeadId) {
      fetchPartnerLeadDetails();
    }
  }, [fetchedPartnerStartedJourney, fetchedLeadId, fetchPartnerLeadDetails]);

  useEffect(() => {
    dispatch(setAdditionalSummaryDocumentsComplete(false));
    dispatch(setAdditionalSummaryConsentComplete(false));
  }, [dispatch]);

  // Whatever tab the user last clicked is written to localStorage (see the Tabs onChange
  // below). Restore it on every mount of this component — covers both a plain page reload
  // and bouncing back from an external redirect (e.g. Aadhaar verify via DigiLocker from the
  // Consent tab), where redux-persist's debounced write can otherwise lose its race with the
  // navigation and leave the stale (Documents) tab active. This key is explicitly cleared at
  // every place the user can (re-)enter this step fresh — the stepper's Previous button, and
  // Continue/Resume Application from the dashboard — so a later fresh entry always starts on
  // the default (Documents) tab instead of restoring a stale choice from an earlier visit.
  useEffect(() => {
    if (!stepper?.trackingId) return;
    const storedTab = localStorage.getItem(`additionalSummaryActiveTab:${stepper.trackingId}`);
    if (storedTab !== null && Number(storedTab) !== activeTab) {
      dispatch(setAdditionalSummaryActiveTab(Number(storedTab)));
    }
  }, [stepper?.trackingId]);

  // Mirrors Summary.tsx's isReturningFromConsentAadhaar handling: ConsentDetails.handleVerifyAadhaar
  // sets this consentAadhaarType:<trackingId> localStorage marker right before redirecting to
  // DigiLocker for an Aadhaar-via-Consent verification, and only clears it once that verification
  // succeeds or fails. If the hard navigation back and redux-persist's rehydration race leaves
  // activeTab reverted to Documents (0) instead of Consent (1), force it back to Consent (1) here —
  // same as the equivalent effect in Summary.tsx, but keyed off this page's own additionalSummaryActiveTab.
  useEffect(() => {
    if (!stepper?.trackingId) return;
    const isReturningFromConsentAadhaar =
      location.search.includes("document_verified=true") &&
      Boolean(localStorage.getItem(`consentAadhaarType:${stepper.trackingId}`));
    if (isReturningFromConsentAadhaar && activeTab !== 1) {
      dispatch(setAdditionalSummaryActiveTab(1));
    }
  }, [stepper?.trackingId, location.search, activeTab, dispatch]);

  const resolvedTabsList = useMemo(() => {
    if (Array.isArray(tabsList) && tabsList.length > 0) return tabsList;
    if (Array.isArray(fetchedTabsList) && fetchedTabsList.length > 0) return fetchedTabsList;
    return ["Applicant"];
  }, [tabsList, fetchedTabsList]);

  const resolvedCoApplicantDetails = useMemo(
    () => coApplicantDetails || fetchedCoApplicantDetails || appLoanData?.coApplicantDetails || {},
    [coApplicantDetails, fetchedCoApplicantDetails, appLoanData?.coApplicantDetails],
  );

  const resolvedMainApplicantDetails = useMemo(
    () => mainApplicantDetails || fetchedMainApplicantDetails || {},
    [mainApplicantDetails, fetchedMainApplicantDetails],
  );

  const resolvedHandleFetchApplicationDetail =
    handleFetchApplicationDetail || fetchApplicationData;

  const resolvedApplicationDetail = applicationDetail || [];
  const resolvedPartnerStartedJourney =
    partnerStartedJourney ??
    fetchedPartnerStartedJourney ??
    stepper?.fetchUserApplicationData?.partnerStartedJourney ??
    false;
  const resolvedApplicationStatus = applicationStatus || tabsState?.applicationStatus || "";
  const resolvedCoApplicantEmploymentType = coApplicantEmploymentType || fetchedCoApplicantEmploymentType || "";
  const resolvedIsPayPartyPresent = Boolean(isPayPartyPresent ?? fetchedIsPayPartyPresent);
  const resolvedIsPayPartyValue = isPayPartyValue || fetchedIsPayPartyValue || "";
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderBottom: "1px solid #e7e9ee",
          mb: 2,
          mt:isXsOrSm ? 4 : 0,
          px: isXsOrSm ? 2 : 1,
          pb: 1.5,
        }}
      >
        <Box sx={{ mb: 1.5, px: 0.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "8px",
              backgroundColor: "#ecfbfb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 20, color: "#1aa4a6" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "Jost, sans-serif",
                fontSize: "15px",
                fontWeight: 600,
                color: "#4b465c",
                lineHeight: 1.2,
              }}
            >
              Documents & Consent
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                fontFamily: "Jost, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                color: "#8c8a97",
              }}
            >
             Documents & Consent Related Details
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={async (_e, newValue) => {
            if (newValue === 1) {
              await fetchApplicationData();
            }
            if (stepper?.trackingId) {
              localStorage.setItem(`additionalSummaryActiveTab:${stepper.trackingId}`, String(newValue));
            }
            dispatch(setAdditionalSummaryActiveTab(newValue));
          }}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 40,
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Jost, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              color: "#6f6b7d",
              border: "1px solid #d7dbe7",
              borderRadius: "20px",
              minHeight: 34,
              py: 0.5,
              px: 1.5,
              mr: 1,
              minWidth: isXsOrSm ? 130 : 142,
              justifyContent: "flex-start",
              gap: 0.8,
              backgroundColor: "#ffffff",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#1aa4a6",
              fontWeight: 600,
              borderColor: "#9fe4e5",
              backgroundColor: "#ecfbfb",
            },
          }}
        >
          <Tab
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Documents"
          />
          <Tab
            icon={<GppGoodOutlinedIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Consent"
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <ApplicantTabs
          applicants={resolvedTabsList}
          applicationDetail={resolvedApplicationDetail}
          mainApplicantDetails={resolvedMainApplicantDetails}
          coApplicantDetails={resolvedCoApplicantDetails}
          handleFetchApplicationDetail={resolvedHandleFetchApplicationDetail}
          partnerStartedJourney={Boolean(resolvedPartnerStartedJourney)}
          dropdownData={dropdownData}
          applicationStatus={resolvedApplicationStatus}
          coApplicantEmploymentType={resolvedCoApplicantEmploymentType}
          isPayPartyPresent={resolvedIsPayPartyPresent}
          isPayPartyValue={resolvedIsPayPartyValue}
          isAdditional={true}
          activeHorizontalIndex={activeHorizontalIndex}
          activeVerticalIndex={activeVerticalIndex}
        />
      )}

      {activeTab === 1 && (
        <ConsentDetails
          formData={consentFormData}
          mainApplicantDetails={resolvedMainApplicantDetails}
          coApplicantDetails={resolvedCoApplicantDetails}
          trackingId={stepper?.trackingId || ""}
          handleFetchApplicationDetail={resolvedHandleFetchApplicationDetail}
          onContinue={() => dispatch(setAdditionalSummaryActiveTab(0))}
          partnerStartedJourney={Boolean(resolvedPartnerStartedJourney)}
        />
      )}
    </Box>
  );
};

export default AdditionalSummaryDetail;
